import { FunctionComponent, useEffect } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import classnames from 'classnames';
import CheckoutItemBuyerQueryRenderer from './components/containers/QueryRenderers/CheckoutItemBuyerQueryRenderer';
import { useDatadogRum } from './helpers/datadog';
import { CheckoutErrorBoundary } from './CheckoutErrorBoundary';
import { usePrerenderedCheckoutStore } from './prerender/prerenderedCheckoutStoreReact';
import { handleBrowserButtons } from './prerender/redirectToPrerenderedCheckout';
import { createCheckoutEnvironment } from './environment';
import { useAuthSync } from './helpers/useAuthSync';
import { Environment } from 'relay-runtime';
import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { removeCookieNotification } from 'dibs-buyer-layout/exports/cookieNotification';
import { CHECKOUT_CANCELATION_POLICY_TEST } from './helpers/checkoutCancelationPolicyTestHelpers';
import { trackCheckoutOptimizationVariant } from './shared/helpers/checkoutOptimizationAbTestHelpers';

import styles from './styles/CheckoutRoot.scss';

type CheckoutRootProps = {
    itemId: string;
    environment?: Environment;
};

const relayEnvironment = createCheckoutEnvironment({ getGraphQLNetworkContext: () => 'checkout' });

export const CheckoutRoot: FunctionComponent<CheckoutRootProps> = props => {
    const { itemId, environment } = props;

    useEffect(() => {
        handleBrowserButtons();
    }, []);

    useAuthSync();

    useDatadogRum({ itemId });

    const { rootState } = usePrerenderedCheckoutStore();

    useEffect(() => {
        if (['NOT_INITIALIZED', 'VISIBLE'].includes(rootState)) {
            trackAbTestV2Variant(CHECKOUT_CANCELATION_POLICY_TEST);
            trackCheckoutOptimizationVariant();
            trackAbTestV2Variant('enhancedBuyerIncentives');

            /**
             * Might throw a warning:
             * `Attempted to synchronously unmount a root while React was already rendering`
             *
             * Should be OK in our case.
             *
             * We don't render the cookie notification on checkout, but if you come e.g.
             * from PDP that rendered it, via the pre-rended method, it can still be visible
             * on checkout.
             */
            removeCookieNotification();
        }
    }, [rootState]);

    if (rootState === 'PENDING') {
        return null;
    }

    const checkoutRelayEnvironment = environment || relayEnvironment;

    return (
        <CheckoutErrorBoundary>
            <div
                className={classnames(styles.wrapper, {
                    [styles.hidden]: rootState === 'ENABLED_IN_BACKGROUND',
                })}
            >
                <RelayEnvironmentProvider environment={checkoutRelayEnvironment}>
                    <CheckoutItemBuyerQueryRenderer itemId={itemId} />
                </RelayEnvironmentProvider>
            </div>
        </CheckoutErrorBoundary>
    );
};

export default CheckoutRoot;
